import { useCallback } from 'react';
import { toast } from 'react-toastify';

const GLOBAL_SUGGESTION_NAV_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'Enter',
  'Escape',
]);

const hasOpenGlobalSuggestionsForEvent = event => {
  if (!GLOBAL_SUGGESTION_NAV_KEYS.has(event.key)) return false;

  const activeCell = event.target?.closest?.('td');
  return Boolean(activeCell?.querySelector?.('.global-formula-suggestions'));
};

export default function useDetailsKeyboardNav({
  detailsRows,
  selectedItem,
  type,
  createSwimlaneDetailsMutation,
  swimlane,
  setDetailsRow,
  reorderRows,
  refetch,
  refetchBudgetId,
  setCaretPosition,
  caretPoition,
  onKeyUp,
}) {
  const handleKeyUp = useCallback(
    event => {
      if (hasOpenGlobalSuggestionsForEvent(event)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const key = event.key;
      const cursorPosition = event.target.selectionStart;
      const value = event.target.value;
      const name = event.target.name;
      const columnkey = name?.split(',')[0];
      const index = parseInt(name?.split(',')[1]);
      const rowsForItem = detailsRows[selectedItem?.id] || [];
      if (
        key === 'ArrowDown' ||
        key === 'ArrowUp' ||
        key === 'ArrowRight' ||
        key === 'ArrowLeft' ||
        key === 'Enter'
      ) {
        const newCaretPosition = `${name},${cursorPosition}`;
        const container = document.getElementsByClassName(
          'budget-swimlane-details-container'
        )?.[0];
        let elem;
        switch (key) {
          case 'ArrowDown':
            elem = document.getElementsByName(`${columnkey},${index + 1}`)?.[0];
            elem && container?.scrollBy({ top: 40, behavior: 'smooth' });
            break;
          case 'ArrowUp':
            elem = document.getElementsByName(`${columnkey},${index - 1}`)?.[0];
            elem && container?.scrollBy({ top: -40, behavior: 'smooth' });
            break;
          case 'ArrowLeft':
            if (
              caretPoition === newCaretPosition ||
              value.length === cursorPosition ||
              value.length === 0
            ) {
              if (columnkey === 'comment') {
                elem = document.getElementsByName(
                  `multiplier_unit,${index}`
                )?.[0];
              } else if (columnkey === 'multiplier_unit') {
                elem = document.getElementsByName(`multiplier,${index}`)?.[0];
              } else if (columnkey === 'multiplier') {
                elem = document.getElementsByName(`rate,${index}`)?.[0];
              } else if (columnkey === 'rate') {
                elem = document.getElementsByName(`unit,${index}`)?.[0];
              } else if (columnkey === 'unit') {
                elem = document.getElementsByName(`quantity,${index}`)?.[0];
              } else if (columnkey === 'quantity') {
                elem = document.getElementsByName(`item,${index}`)?.[0];
              } else if (columnkey === 'item') {
                onKeyUp?.(event);
                return;
              }
            } else {
              setCaretPosition(newCaretPosition);
            }
            break;
          case 'ArrowRight':
            if (
              caretPoition !== null &&
              (caretPoition === newCaretPosition || value.length === 0)
            ) {
              if (columnkey === 'item') {
                elem = document.getElementsByName(`quantity,${index}`)?.[0];
              } else if (columnkey === 'quantity') {
                elem = document.getElementsByName(`unit,${index}`)?.[0];
              } else if (columnkey === 'unit') {
                elem = document.getElementsByName(`rate,${index}`)?.[0];
              } else if (columnkey === 'rate') {
                elem = document.getElementsByName(`multiplier,${index}`)?.[0];
              } else if (columnkey === 'multiplier') {
                elem = document.getElementsByName(
                  `multiplier_unit,${index}`
                )?.[0];
              } else if (columnkey === 'multiplier_unit') {
                elem = document.getElementsByName(`comment,${index}`)?.[0];
              }
              if (elem?.value?.length !== 0) {
                setCaretPosition(null);
              }
            } else {
              setCaretPosition(newCaretPosition);
            }
            break;
          case 'Enter': {
            // We need to be able to create a new row from ANY column,
            // but avoid the buggy behaviour and duplicates:
            // - Treat plain Enter and Shift+Enter the same (both add/navigate).
            // - Ignore Ctrl/Alt/Meta modified Enter.
            if (event.ctrlKey || event.altKey || event.metaKey) {
              onKeyUp?.(event);
              return;
            }

            event.preventDefault();

            const isLastRow = index === rowsForItem.length - 1;

            // If we're NOT on the last row, just move to the same column
            // on the next row. This lets you use Enter/Shift+Enter to step
            // through existing rows without inserting extras in the middle.
            if (!isLastRow) {
              elem = document.getElementsByName(
                `${columnkey},${index + 1}`
              )?.[0];
              elem && container?.scrollBy({ top: 40, behavior: 'smooth' });
              break;
            }

            // We're on the last row: insert a new row below, regardless of
            // which column you're currently editing (item, rate, quantity, etc.).
            const nextIndex = index + 1;
            const newRowData = {
              row_order: rowsForItem.length + 1,
              item: '',
              position: rowsForItem.length,
              multiplier: '1',
              quantity: '1',
              id: `temp-${type}-${selectedItem?.id}-${nextIndex}`,
            };

            createSwimlaneDetailsMutation.mutate(
              { details: { ...newRowData }, selectedItem, swimlane, type },
              {
                onSuccess: response => {
                  const newRow = response.data || newRowData;
                  setDetailsRow(prev => {
                    const updatedRows = [...prev[selectedItem?.id]];
                    updatedRows.splice(nextIndex, 0, newRow);
                    const adjustedRows = updatedRows.map((row, i) => ({
                      ...row,
                      position: i,
                      row_order: i + 1,
                    }));
                    reorderRows(adjustedRows);
                    container?.scrollBy({ top: 40, behavior: 'smooth' });
                    setTimeout(() => {
                      document
                        .getElementsByName(`${columnkey},${nextIndex}`)[0]
                        ?.focus();
                    }, 0);
                    return { ...prev, [selectedItem?.id]: adjustedRows };
                  });
                  refetch?.();
                  refetchBudgetId?.();
                },
                onError: () => toast.error('Error in adding new row'),
              }
            );
            break;
          }
          default:
        }
        elem?.focus();
        elem?.select();
      }
    },
    [
      caretPoition,
      detailsRows,
      onKeyUp,
      reorderRows,
      selectedItem,
      setCaretPosition,
      setDetailsRow,
      swimlane,
      type,
      createSwimlaneDetailsMutation,
      refetch,
      refetchBudgetId,
    ]
  );

  return handleKeyUp;
}
